import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LookupSearchField from './tablemaint/LookupSearchField';
import LookupResultCard from '../pages/order-management/components/LookupResultCard';
import { searchPorts, type PortLookupRow } from '../services/ports.service';

/**
 * Reusable enriched Port picker: search cms.ports (name / UN-LOCODE / recno) and store the
 * port's recno, showing the port name. Built on the shared <LookupSearchField> + the
 * enriched <LookupResultCard> (same look as the Bill To / Customer typeaheads). Used by
 * the EDI Port Mapping drawer and both EDI create dialogs; drop it in anywhere a record
 * needs a CMS-port FK. `hideLabel`/`hideStatus` strip it down to the bare input for a
 * table cell, matching <LocationLookupField>.
 */
export interface PortValue {
  recno: number; // 0 = unmapped
  name: string;
}

const EMPTY: PortValue = { recno: 0, name: '' };

interface Props {
  value: PortValue;
  onChange: (v: PortValue) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  /** Compact table-cell mode: suppress the field label (column header rendered once). */
  hideLabel?: boolean;
  /** Compact table-cell mode: suppress the "Mapped to #recno / Not mapped" status line. */
  hideStatus?: boolean;
  /** Orange-highlight the input — the stored reference is deleted or no longer
   *  exists (same styling the plain selects use for an unknown value). */
  invalid?: boolean;
}

export const PortLookupField: React.FC<Props> = ({ value, onChange, label = 'Port', disabled, required, hideLabel, hideStatus, invalid }) => (
  <div>
    <LookupSearchField<PortLookupRow>
      invalid={invalid}
      label={label}
      hideLabel={hideLabel}
      required={required}
      displayValue={value.name}
      // Typing (without picking) unlinks — the user must pick a result to map.
      onType={(text) => onChange({ recno: 0, name: text })}
      onSelect={(p) => onChange({ recno: p.recno, name: p.pport })}
      search={searchPorts}
      renderItem={(p) => (
        <LookupResultCard
          name={p.pport}
          code={p.recno}
          details={[
            { label: 'Country', value: p.pcountry },
            { label: 'Continent', value: p.pcontinent },
            { label: 'Seaport Code', value: p.seaportcode },
            { label: 'UN/LOCODE', value: p.unlocode },
          ]}
        />
      )}
      itemValue={(p) => String(p.recno)}
      itemLabel={(p) => p.pport}
      disabled={disabled}
      placeholder="Search ports by name, UN/LOCODE, or #…"
    />
    {!hideStatus && (
    <div className="mt-1.5 flex items-center gap-3 text-xs">
      {value.recno > 0 ? (
        <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
          Mapped to #{value.recno}
          {!disabled && (
            <button type="button" onClick={() => onChange(EMPTY)} title="Clear mapping"
              className="ml-1 text-gray-400 hover:text-red-500">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      ) : (
        <span className="text-muted">Not mapped to a CMS port</span>
      )}
    </div>
    )}
  </div>
);

export default PortLookupField;
